/**
 * 로그인 폼: Form.ts로 AJAX 제출.
 * 성공 시 redirect, 실패 시 #auth-login-error 표시 + require_captcha면 캡차 표시.
 * Form must have id="auth-login-form", data-submit-url, data-captcha-url, data-require-captcha.
 */
import { Form } from "../../core/Form";

const FORM_ID = "auth-login-form";
const ERROR_BOX_ID = "auth-login-error";
const SAVED_EMAIL_KEY = "auth_saved_email";

function getForm(): HTMLFormElement | null {
  return document.getElementById(FORM_ID) as HTMLFormElement | null;
}

function getEmailInput(): HTMLInputElement | null {
  return getForm()?.querySelector('input[name="email"]') as HTMLInputElement | null;
}

function getSaveIdCheckbox(): HTMLInputElement | null {
  return document.querySelector(`#${FORM_ID} [data-save-id]`) as HTMLInputElement | null;
}

function getErrorBox(): HTMLElement | null {
  return document.getElementById(ERROR_BOX_ID);
}

function getCaptchaWrap(): HTMLElement | null {
  return document.getElementById("auth-login-captcha-wrap");
}

function getCaptchaInput(): HTMLInputElement | null {
  return document.getElementById("auth-login-captcha") as HTMLInputElement | null;
}

function getCaptchaImg(): HTMLImageElement | null {
  return document.getElementById("auth-login-captcha-img") as HTMLImageElement | null;
}

function setCaptchaVisible(visible: boolean): void {
  const wrap = getCaptchaWrap();
  const input = getCaptchaInput();
  if (!wrap || !input) return;
  if (visible) {
    wrap.hidden = false;
    input.required = true;
    refreshCaptchaImage();
  } else {
    wrap.hidden = true;
    input.required = false;
    input.value = "";
  }
}

function refreshCaptchaImage(): void {
  const form = getForm();
  const img = getCaptchaImg();
  if (!form || !img) return;
  const url = form.dataset.captchaUrl;
  if (url) img.src = `${url}?v=${Date.now()}`;
}

function renderError(message: string | null): void {
  const box = getErrorBox();
  if (!box) return;
  if (message === null || message === "") {
    box.innerHTML = "";
    box.setAttribute("hidden", "");
    return;
  }
  const wrap = document.createElement("div");
  wrap.className = "auth-page__error";
  wrap.textContent = message;
  box.innerHTML = "";
  box.appendChild(wrap);
  box.removeAttribute("hidden");
}

function getFirstError(errors: Record<string, string[]> | undefined): string {
  if (!errors || typeof errors !== "object") return "";
  const first = Object.values(errors).flat()[0];
  return typeof first === "string" ? first : "";
}

function isKo(): boolean {
  return document.documentElement.lang?.startsWith("ko") ?? false;
}

function defaultMessage(key: "invalid_response" | "request_failed" | "login_failed"): string {
  if (isKo()) {
    const ko: Record<typeof key, string> = {
      invalid_response: "응답을 처리할 수 없습니다.",
      request_failed: "요청을 보낼 수 없습니다.",
      login_failed: "로그인에 실패했습니다.",
    };
    return ko[key];
  }
  const en: Record<typeof key, string> = {
    invalid_response: "Invalid response.",
    request_failed: "Request failed.",
    login_failed: "Login failed.",
  };
  return en[key];
}

export function initAuthLoginForm(): void {
  const form = getForm();
  if (!form) return;

  const submitUrl = form.dataset.submitUrl ?? form.action;
  if (!submitUrl) return;

  // 아이디 저장: 저장된 이메일이 있으면 입력란에 채우고 체크
  try {
    const saved = localStorage.getItem(SAVED_EMAIL_KEY);
    const emailInput = getEmailInput();
    const saveIdCheck = getSaveIdCheckbox();
    if (saved && emailInput && saveIdCheck) {
      emailInput.value = saved;
      saveIdCheck.checked = true;
    }
  } catch {
    // localStorage 비활성화 등
  }

  const requireCaptchaInitial = form.dataset.requireCaptcha === "1";
  if (requireCaptchaInitial) {
    setCaptchaVisible(true);
  }

  const refreshBtn = document.getElementById("auth-login-captcha-refresh");
  if (refreshBtn) {
    refreshBtn.addEventListener("click", () => refreshCaptchaImage());
  }

  form.addEventListener("submit", () => {
    const emailInput = getEmailInput();
    const saveIdCheck = getSaveIdCheckbox();
    try {
      if (saveIdCheck?.checked && emailInput?.value?.trim()) {
        localStorage.setItem(SAVED_EMAIL_KEY, emailInput.value.trim());
      } else {
        localStorage.removeItem(SAVED_EMAIL_KEY);
      }
    } catch {
      // ignore
    }
  });

  new Form(form, {
    submitUrl,
    submitButtonSelector: "button[type=submit]",
    loaderSelector: ".auth-page__btn-loader",
    requestFailedMessage: defaultMessage("request_failed"),
    invalidResponseMessage: defaultMessage("invalid_response"),
    onSuccess: (data) => {
      const redirect = (data?.data as { redirect?: string } | undefined)?.redirect;
      if (redirect) {
        window.location.href = redirect;
      }
    },
    onFail: (message, data) => {
      const d = data?.data as { require_captcha?: boolean } | undefined;
      const errors = data?.errors as Record<string, string[]> | undefined;
      if (d?.require_captcha === true) {
        setCaptchaVisible(true);
      }
      const first = getFirstError(errors);
      renderError(first || message || defaultMessage("login_failed"));
    },
  }).init();

  form.addEventListener("submit", () => renderError(null));
}
